import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: any) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface OAuthUserInfo {
  provider: string;
  oauth_id: string;
  profile_picture?: string;
}

export interface OAuthStatus {
  has_oauth: boolean;
  oauth_info?: OAuthUserInfo;
  can_unlink: boolean;
}

export interface OAuthAnalytics {
  google_users_count: number;
  facebook_users_count: number;
  recent_sessions_count: number;
  total_oauth_users: number;
}

export interface OAuthConfig {
  oauth_available: boolean;
  providers: {
    [key: string]: {
      configured: boolean;
      client_id_configured: boolean;
      client_secret_configured: boolean;
      features: string[];
    };
  };
  security_features: string[];
}

class AdvancedOAuthService {
  private oauthWindow: Window | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private intervalId: NodeJS.Timeout | null = null;
  // private oauthPromise: Promise<any> | null = null;

  // Initiate advanced OAuth login with popup
  async initiateOAuthLogin(provider: 'google' | 'facebook', usePopup: boolean = true, userType?: string): Promise<any> {
    try {
      if (usePopup) {
        try {
          return await this.initiateOAuthPopup(provider, userType);
        } catch (popupError: any) {
          // Fallback to redirect if popup fails
          return await this.initiateOAuthRedirect(provider, userType);
        }
      } else {
        return await this.initiateOAuthRedirect(provider, userType);
      }
    } catch (error: any) {
      throw new Error(`Failed to initiate ${provider} login: ${error.message}`);
    }
  }

  // Initiate OAuth login with popup window
  private async initiateOAuthPopup(provider: 'google' | 'facebook', userType?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // Close any existing popup
      if (this.oauthWindow && !this.oauthWindow.closed) {
        this.oauthWindow.close();
      }
      // Clear any lingering timers from previous attempts
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }

      // Open popup window
      const popupWidth = 500;
      const popupHeight = 600;
      const left = window.screenX + (window.outerWidth - popupWidth) / 2;
      const top = window.screenY + (window.outerHeight - popupHeight) / 2;

      // Build OAuth URL with user type and redirect hints
      const redirectAfter = userType === 'agencies' ? '/research-landing' : '/optimize';
      const frontendOrigin = window.location.origin;
      const params = new URLSearchParams();
      if (userType) params.set('user_type', userType);
      params.set('redirect_after_login', redirectAfter);
      params.set('frontend_origin', frontendOrigin);
      const oauthUrl = `${API_BASE_URL}/advanced-oauth/login/${provider}?${params.toString()}`;

      this.oauthWindow = window.open(
        oauthUrl,
        'oauth_popup',
        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      if (!this.oauthWindow) {
        reject(new Error('Popup blocked by browser. Please allow popups and try again.'));
        return;
      }

              // Listen for messages from popup
        const apiOrigin = new URL(API_BASE_URL).origin;
        const messageListener = (event: MessageEvent) => {
          // Accept messages from backend API origin or same-origin
          if (event.origin !== apiOrigin && event.origin !== window.location.origin) {
            return;
          }


        if (event.data.type === 'OAUTH_SUCCESS') {
          window.removeEventListener('message', messageListener);
          clearInterval(checkClosed);
          
          // Store auth data
          if (event.data.token) {
            localStorage.setItem('authToken', event.data.token);
            localStorage.setItem('user', JSON.stringify(event.data.user));
          }
          
          // Close popup if still open
          if (this.oauthWindow && !(this.oauthWindow as any).closed) {
            try {
              (this.oauthWindow as any).close();
            } catch (e) {
            }
          }
          this.oauthWindow = null;
          
          resolve(event.data);
        } else if (event.data.type === 'OAUTH_ERROR') {
          window.removeEventListener('message', messageListener);
          clearInterval(checkClosed);
          
          if (this.oauthWindow && !(this.oauthWindow as any).closed) {
            try {
              (this.oauthWindow as any).close();
            } catch (e) {
            }
          }
          this.oauthWindow = null;
          
          reject(new Error(event.data.error || 'OAuth authentication failed'));
        }
      };

      window.addEventListener('message', messageListener);

      // Check if popup was closed
      const checkClosed = setInterval(() => {
        if (!this.oauthWindow || (this.oauthWindow as any).closed) {
          if (this.intervalId) { clearInterval(this.intervalId); this.intervalId = null; }
          if (this.timeoutId) { clearTimeout(this.timeoutId); this.timeoutId = null; }
          window.removeEventListener('message', messageListener);
          reject(new Error('OAuth popup was closed'));
        }
      }, 1000);
      this.intervalId = checkClosed;

      // Timeout after 10 minutes
      setTimeout(() => {
        if (this.oauthWindow && !(this.oauthWindow as any).closed) {
          try {
            (this.oauthWindow as any).close();
          } catch (e) {
          }
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          reject(new Error('OAuth authentication timed out'));
        }
      }, 600000);
    });
  }

  // Initiate OAuth login with redirect
  private async initiateOAuthRedirect(provider: 'google' | 'facebook', userType?: string): Promise<void> {
    // Store current location for redirect after OAuth
    localStorage.setItem('oauth_redirect_after_login', window.location.pathname);
    
    // Build OAuth URL with user type and redirect hints
    const redirectAfter = userType === 'agencies' ? '/research-landing' : '/optimize';
    const frontendOrigin = window.location.origin;
    const params = new URLSearchParams();
    if (userType) params.set('user_type', userType);
    params.set('redirect_after_login', redirectAfter);
    params.set('frontend_origin', frontendOrigin);
    const oauthUrl = `${API_BASE_URL}/advanced-oauth/login/${provider}?${params.toString()}`;
    
    // Redirect to OAuth endpoint
    window.location.href = oauthUrl;
  }

  // Handle OAuth callback (for redirect flow)
  async handleOAuthCallback(provider: 'google' | 'facebook'): Promise<any> {
    try {
      const response = await api.get(`/advanced-oauth/callback/${provider}`);
      
      // Store auth data
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(`OAuth callback failed: ${error.response?.data?.detail || error.message}`);
    }
  }

  // Link OAuth account to existing user
  async linkOAuthAccount(provider: 'google' | 'facebook'): Promise<any> {
    try {
      const response = await api.post(`/advanced-oauth/link/${provider}`);
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to link ${provider} account: ${error.response?.data?.detail || error.message}`);
    }
  }

  // Unlink OAuth account
  async unlinkOAuthAccount(): Promise<any> {
    try {
      const response = await api.post('/advanced-oauth/unlink');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to unlink OAuth account: ${error.response?.data?.detail || error.message}`);
    }
  }

  // Get OAuth status for current user
  async getOAuthStatus(): Promise<OAuthStatus> {
    try {
      const response = await api.get('/advanced-oauth/status');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get OAuth status: ${error.response?.data?.detail || error.message}`);
    }
  }

  // Get OAuth analytics (admin only)
  async getOAuthAnalytics(): Promise<OAuthAnalytics> {
    try {
      const response = await api.get('/advanced-oauth/analytics');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get OAuth analytics: ${error.response?.data?.detail || error.message}`);
    }
  }

  // Get OAuth configuration
  async getOAuthConfig(): Promise<OAuthConfig> {
    try {
      const response = await api.get('/advanced-oauth/config');
      return response.data;
    } catch (error: any) {
      throw new Error(`Failed to get OAuth config: ${error.response?.data?.detail || error.message}`);
    }
  }

  // Check if OAuth callback URL contains tokens
  checkForOAuthTokens(): { provider?: string; token?: string; error?: string; source?: 'query' | 'hash' } | null {
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    // Check for OAuth tokens in URL parameters
    const direct_token = urlParams.get('token');
    const access_token = urlParams.get('access_token') || hashParams.get('access_token');
    const error = urlParams.get('error') || hashParams.get('error');
    const provider = urlParams.get('provider') || hashParams.get('provider');
    
    if (direct_token) {
      return { provider: provider ?? undefined, token: direct_token ?? undefined, source: 'query' };
    } else if (access_token) {
      return { provider: provider ?? undefined, token: access_token ?? undefined, source: 'hash' };
    } else if (error) {
      return { error: error ?? undefined, provider: provider ?? undefined };
    }
    
    return null;
  }

  // Clear OAuth tokens from URL
  clearOAuthTokens(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('access_token');
    url.searchParams.delete('error');
    url.searchParams.delete('provider');
    url.searchParams.delete('state');
    url.hash = '';
    window.history.replaceState({}, document.title, url.toString());
  }

  // Get OAuth provider display name
  getProviderDisplayName(provider: string): string {
    switch (provider.toLowerCase()) {
      case 'google':
        return 'Google';
      case 'facebook':
        return 'Facebook';
      default:
        return provider;
    }
  }

  // Get OAuth provider icon
  getProviderIcon(provider: string): string {
    switch (provider.toLowerCase()) {
      case 'google':
        return 'https://www.svgrepo.com/show/475656/google-color.svg';
      case 'facebook':
        return 'https://www.svgrepo.com/show/475647/facebook-color.svg';
      default:
        return '';
    }
  }

  // Get OAuth provider color
  getProviderColor(provider: string): string {
    switch (provider.toLowerCase()) {
      case 'google':
        return '#4285F4';
      case 'facebook':
        return '#1877F2';
      default:
        return '#6B7280';
    }
  }

  // Validate OAuth configuration
  async validateOAuthConfig(): Promise<{ valid: boolean; errors: string[] }> {
    try {
      const config = await this.getOAuthConfig();
      const errors: string[] = [];

      if (!config.oauth_available) {
        errors.push('OAuth service is not available');
      }

      Object.entries(config.providers).forEach(([provider, providerConfig]) => {
        if (!providerConfig.configured) {
          errors.push(`${provider} OAuth is not properly configured`);
        }
        if (!providerConfig.client_id_configured) {
          errors.push(`${provider} client ID is missing`);
        }
        if (!providerConfig.client_secret_configured) {
          errors.push(`${provider} client secret is missing`);
        }
      });

      return {
        valid: errors.length === 0,
        errors
      };
    } catch (error: any) {
      return {
        valid: false,
        errors: [error.message]
      };
    }
  }

  // Handle OAuth errors
  handleOAuthError(error: any): string {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    }
    
    if (error.message.includes('popup blocked')) {
      return 'Popup was blocked by your browser. Please allow popups and try again.';
    }
    
    if (error.message.includes('timed out')) {
      return 'OAuth authentication timed out. Please try again.';
    }
    
    return error.message || 'OAuth authentication failed';
  }

  // Check if OAuth is supported
  isOAuthSupported(): boolean {
    return typeof window !== 'undefined' && window.location.protocol === 'https:';
  }

  // Get OAuth redirect URL
  getOAuthRedirectUrl(provider: string): string {
    return `${window.location.origin}/oauth-callback?provider=${provider}`;
  }
}

export const advancedOAuthService = new AdvancedOAuthService();
export default advancedOAuthService;
